import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Company } from 'src/app/domains/companies/models';
import { FeatureFlagsService } from 'src/app/domains/feature-flags/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { FeatureFlagCompany, FeatureFlagOverview } from 'voxloud-types/dist/models';

@Component({
  selector: 'gdp-flag-usage-modal',
  templateUrl: './flag-usage-modal.component.html',
  styleUrls: ['./flag-usage-modal.component.scss']
})
export class FlagUsageModalComponent implements OnInit {
  private readonly activeModal: NgbActiveModal = inject(NgbActiveModal);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly notification: NotificationService = inject(NotificationService);

  @Input() flagId: number;

  public flagOverview: FeatureFlagOverview;
  public loading: boolean = false;
  public saveLoading: boolean = false;
  public apiFailed: boolean = false;
  public hasChanges: boolean = false;
  public companyControl: FormControl = new FormControl();
  public selectedCompany: FeatureFlagCompany | null = null;

  ngOnInit(): void {
    this.fetchFlagOverview();
    this.companyControl.valueChanges.subscribe({
      next: (company: Company) => {
        if (!company) {
          this.selectedCompany = null;
          return;
        }
        this.selectedCompany = {
          company_id: company.companyId,
          name: company.name,
          alphaname: company.alphaname,
          email: company.email
        };
      }
    })
  }

  public fetchFlagOverview() {
    this.loading = true;
    this.apiFailed = false;
    this.featureFlagsService.getFeatureFlagOverview(this.flagId).subscribe({
      next: (res: FeatureFlagOverview) => {
        this.flagOverview = res;
        this.loading = false;
      },
      error: () => {
        this.apiFailed = true
        this.loading = false;
      }
    })
  }

  public addCompany() {
    if (!this.selectedCompany || this.companyAlreadyAdded) return;

    this.flagOverview.enabled_companies.push(this.selectedCompany);
    this.refreshUsageCounters();
    this.selectedCompany = null;
    this.hasChanges = true;
    this.companyControl.reset();
  }

  public removeCompany(company: FeatureFlagCompany) {
    this.flagOverview.enabled_companies = this.flagOverview.enabled_companies.filter(
      (c) => c.company_id !== company.company_id
    );
    this.refreshUsageCounters();
    this.hasChanges = true;
  }

  private refreshUsageCounters(): void {
    this.flagOverview.total_enabled_companies = this.flagOverview.enabled_companies.length;
    this.flagOverview.enabled_percentage = this.flagOverview.total_companies
      ? Math.round((this.flagOverview.total_enabled_companies / this.flagOverview.total_companies) * 100)
      : 0;
  }

  public get companyAlreadyAdded(): boolean {
    if (!this.selectedCompany || !this.flagOverview) {
      return false;
    }
    return this.flagOverview.enabled_companies.some(
      c => c.company_id === this.selectedCompany?.company_id
    );
  }

  public save(): void {
    if (this.saveLoading) return;
    this.saveLoading = true;
    const company_ids = this.flagOverview.enabled_companies.map(c => c.company_id);
    this.featureFlagsService.updateFlagCompanies(this.flagId, { company_ids }).subscribe({
      next: () => {
        this.saveLoading = false;
        this.notification.success('internal-facilities.feature-flags.success.save-companies');
        this.close();
      },
      error: () => {
        this.saveLoading = false;
      }
    });
  }

  public close(): void {
    this.activeModal.close();
  }
}